import { useEffect, useMemo, useRef, useState } from 'react';

import { useGetCampaignScheduleImportCandidates } from '~/hooks/apis/offers/useGetCampaignScheduleImportCandidates';
import { useImportCampaignSchedules } from '~/hooks/apis/offers/useImportCampaignSchedules';

import ApiError from '~/types/ApiError';

import { groupCandidatesByRegion } from '../Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/campaignScheduleCandidateGroups';
import type {
  CampaignScheduleImportCandidatesResponse,
  ImportBlocker,
} from '../Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/types';

function getImportBlockers(error: unknown): Array<ImportBlocker> | undefined {
  if (!(error instanceof ApiError)) {
    return undefined;
  }
  const body = error.responseBody as { blockers?: Array<ImportBlocker> } | undefined;
  return body?.blockers;
}

export function useCampaignScheduleImportSelection(
  offerId: string | undefined,
  importCandidates: CampaignScheduleImportCandidatesResponse | undefined,
  open: boolean,
) {
  const candidates = importCandidates?.candidates ?? [];
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const importMutation = useImportCampaignSchedules(offerId);
  const wasOpen = useRef(open);

  // Gated on candidates.length to avoid a "nothing selected" flash before selectedKeys populates.
  const { data: liveCompleteness } = useGetCampaignScheduleImportCandidates(offerId, {
    enabled: open && candidates.length > 0,
    selectedKeys: Array.from(selectedKeys),
  });

  useEffect(() => {
    // Excludes `candidates` changing after import (cache invalidation) from re-triggering this reset.
    if (open && !wasOpen.current) {
      // Conflicting candidates are selectable but never auto-selected — overwriting is opt-in.
      setSelectedKeys(
        new Set(
          candidates
            .filter((candidate) => !candidate.isMatched && !candidate.dependencyError && !candidate.conflictingSchedule)
            .map((candidate) => candidate.key),
        ),
      );
      importMutation.reset();
    }
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, candidates]);

  const toggleKeys = (keys: Array<string>, select: boolean) => {
    setSelectedKeys((current) => {
      const next = new Set(current);
      keys.forEach((key) => (select ? next.add(key) : next.delete(key)));
      return next;
    });
  };

  const selectableCandidates = useMemo(
    () => candidates.filter((candidate) => !candidate.isMatched && !candidate.dependencyError),
    [candidates],
  );
  const allSelected =
    selectableCandidates.length > 0 && selectableCandidates.every((candidate) => selectedKeys.has(candidate.key));
  const someSelected = !allSelected && selectableCandidates.some((candidate) => selectedKeys.has(candidate.key));

  const toggleSelectAll = () => {
    setSelectedKeys(allSelected ? new Set() : new Set(selectableCandidates.map((candidate) => candidate.key)));
  };

  const handleImport = () => {
    const selectedCandidates = candidates.filter((candidate) => selectedKeys.has(candidate.key));
    importMutation.mutate(selectedCandidates);
  };

  const resultByKey = useMemo(
    () => new Map((importMutation.data?.results ?? []).map((result) => [result.candidate.key, result])),
    [importMutation.data],
  );
  const candidateGroups = useMemo(() => groupCandidatesByRegion(candidates, resultByKey), [candidates, resultByKey]);
  const hasCompletenessIssue = !!liveCompleteness && !liveCompleteness.canImport;
  // Nothing selected yet means these blockers describe pre-existing data, not the current
  // selection — fold them into the informational warnings instead of "Import blocked".
  const completenessWarnings =
    selectedKeys.size === 0 && hasCompletenessIssue ? liveCompleteness.blockers.map((blocker) => blocker.message) : [];
  const allWarnings = [...(liveCompleteness?.warnings ?? importCandidates?.warnings ?? []), ...completenessWarnings];
  const skippedKeys = importMutation.data?.skippedKeys ?? [];
  const importBlockers = importMutation.isError ? getImportBlockers(importMutation.error) : undefined;
  const liveBlockers = selectedKeys.size > 0 && hasCompletenessIssue ? liveCompleteness.blockers : undefined;
  const conflictingTripleKeys = useMemo(() => {
    const keys = new Set<string>();
    (liveBlockers ?? []).forEach((blocker) => {
      if (blocker.code === 'conflicting_source_records' && blocker.type && blocker.region) {
        keys.add(`${blocker.type}::${blocker.brand}::${blocker.region}`);
      }
    });
    return keys;
  }, [liveBlockers]);

  return {
    allSelected,
    allWarnings,
    candidateGroups,
    candidates,
    conflictingTripleKeys,
    handleImport,
    importBlockers,
    importMutation,
    liveBlockers,
    resultByKey,
    selectableCandidates,
    selectedKeys,
    skippedKeys,
    someSelected,
    toggleKeys,
    toggleSelectAll,
  };
}
